/**
 * Members card (UI_SDD §R.8, DEC-140) — the invitation surface.
 *
 * Lives on the Settings page rather than a new page because Settings already
 * carries the `profile = admin` gate and the invitation endpoints carry the same
 * one. A second gated page would be a second thing to keep in agreement.
 */
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Copy, Check } from 'lucide-react'
import { membersApi } from '../../api/members'
import type { InviteResult } from '../../api/members'
import { Button } from '../shared/Button'
import { Spinner } from '../shared/Spinner'

const PROFILES = [
  { id: 'engineering', label: 'Engineering & IT' },
  { id: 'executive', label: 'Executive' },
  { id: 'product_gtm', label: 'Product & GTM' },
  { id: 'admin', label: 'Admin' },
]

const STATE_LABEL: Record<string, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  expired: 'Expired',
  revoked: 'Cancelled',
  invited: 'Invited',
  active: 'Active',
  disabled: 'Disabled',
}

function profileLabel(id: string | null): string {
  return PROFILES.find((p) => p.id === id)?.label ?? id ?? 'Unknown'
}

export function MembersCard() {
  const queryClient = useQueryClient()
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [profile, setProfile] = useState('engineering')
  const [issued, setIssued] = useState<InviteResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [confirmingDisable, setConfirmingDisable] = useState<string | null>(null)

  const members = useQuery({
    queryKey: ['members'],
    queryFn: () => membersApi.listMembers(),
  })
  const invitations = useQuery({
    queryKey: ['invitations'],
    queryFn: () => membersApi.listInvitations(),
  })

  function refetchAll() {
    queryClient.invalidateQueries({ queryKey: ['members'] })
    queryClient.invalidateQueries({ queryKey: ['invitations'] })
  }

  const invite = useMutation({
    mutationFn: () => membersApi.invite(email.trim(), profile, name.trim()),
    onSuccess: (result) => {
      setIssued(result)
      setEmail('')
      setName('')
      setError(null)
      setCopied(false)
      refetchAll()
    },
    onError: (err: unknown) => {
      const message = err instanceof Error ? err.message : ''
      if (message.includes('409') || message.toLowerCase().includes('different workspace')) {
        setError('That address already belongs to a different workspace.')
      } else if (message.toLowerCase().includes('valid email')) {
        setError('Enter a valid email address.')
      } else if (message.includes('503')) {
        setError('Invitation links are not configured on this server. Set TDIA_PUBLIC_URL.')
      } else {
        setError('Could not create the invitation. Try again in a moment.')
      }
    },
  })

  const revoke = useMutation({
    mutationFn: (linkId: string) => membersApi.revokeInvitation(linkId),
    onSuccess: refetchAll,
  })

  const disable = useMutation({
    mutationFn: (userId: string) => membersApi.disable(userId),
    onSuccess: () => {
      setConfirmingDisable(null)
      refetchAll()
    },
  })

  async function copyLink() {
    if (!issued) return
    await navigator.clipboard.writeText(issued.invite_url)
    setCopied(true)
  }

  return (
    <div className="border border-hairline bg-panel rounded-lg shadow-card p-5">
      {/* DEC-199: no "Members" heading here; the Settings section title already says it. */}
      <p className="text-[12px] text-muted mb-4">
        People you invite receive a single-use link by email. TDIA has no passwords and
        no self-signup.
      </p>

      {/* ── Invite form ──────────────────────────────────────────── */}
      <form
        className="grid gap-2 sm:grid-cols-[1fr_auto_auto] items-end mb-4"
        onSubmit={(e) => {
          e.preventDefault()
          if (email.trim()) invite.mutate()
        }}
      >
        <div>
          <label htmlFor="invite-email" className="block text-[12px] text-body mb-1">
            Work email
          </label>
          <input
            id="invite-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            className="w-full border border-control rounded-md px-2.5 py-1.5 text-[13px] text-ink placeholder-faint focus:outline-none focus-visible:shadow-focus"
          />
        </div>
        <div>
          <label htmlFor="invite-profile" className="block text-[12px] text-body mb-1">
            Profile
          </label>
          <select
            id="invite-profile"
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
            className="border border-control rounded-md px-2.5 py-1.5 text-[13px] text-ink bg-panel focus:outline-none focus-visible:shadow-focus"
          >
            {PROFILES.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </select>
        </div>
        <Button type="submit" disabled={!email.trim()} loading={invite.isPending}>
          Send invitation
        </Button>
      </form>

      {error && <p className="text-[12px] text-critical mb-3">{error}</p>}

      {/* ── The issued link ──────────────────────────────────────────
          Shown with a copy action because the API returns it: the default email
          backend sends nothing, and an operator sending it by hand is a
          supported path, not a fallback (DEC-140). */}
      {issued && (
        <div className="border border-hairline rounded-md p-3 mb-4 bg-fill-subtle">
          <div className="text-[12px] text-body mb-2">
            Invitation created for <span className="font-semibold">{issued.email}</span> as{' '}
            {profileLabel(issued.profile)}.
            {issued.email_sent
              ? ' The email is on its way.'
              : ' No email was sent, so send this link yourself.'}
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 min-w-0 truncate tabular-nums text-[11px] text-muted bg-panel border border-hairline rounded px-2 py-1.5">
              {issued.invite_url}
            </code>
            <button
              type="button"
              onClick={() => void copyLink()}
              className="flex-none flex items-center gap-1 text-[12px] text-muted hover:text-accent-vivid transition-colors"
            >
              {copied ? <Check className="w-[13px] h-[13px]" /> : <Copy className="w-[13px] h-[13px]" />}
              {copied ? 'Copied' : 'Copy'}
            </button>
          </div>
          {issued.email_notice && !issued.email_sent && (
            <p className="text-[11px] text-faint mt-2">{issued.email_notice}</p>
          )}
          {/* DEC-142: advisory. Inviting an external reviewer is legitimate; doing
              it by accident is the common case. */}
          {issued.domain_notice && (
            <p className="text-[11px] text-warn mt-2">{issued.domain_notice}</p>
          )}
        </div>
      )}

      {/* ── Members ──────────────────────────────────────────────── */}
      <h3 className="text-[12px] font-semibold text-body mb-2">People</h3>
      {members.isLoading ? (
        <Spinner />
      ) : (
        <div className="divide-y divide-hairline border border-hairline rounded-md mb-5">
          {(members.data?.members ?? []).map((m) => (
            <div key={m.user_id} className="px-3 py-2.5">
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-ink truncate">
                    {m.name ?? m.email ?? m.user_id}
                  </div>
                  <div className="text-[11px] text-muted truncate">
                    {profileLabel(m.profile)}
                    {' · '}
                    {STATE_LABEL[m.status] ?? m.status}
                    {m.last_login_at
                      ? ` · last signed in ${new Date(m.last_login_at).toLocaleDateString()}`
                      : ''}
                  </div>
                </div>
                {m.status !== 'disabled' && (
                  <button
                    type="button"
                    onClick={() => setConfirmingDisable(m.user_id)}
                    className="flex-none text-[12px] text-muted hover:text-critical transition-colors"
                  >
                    Remove access
                  </button>
                )}
              </div>

              {/* §38.7: a legacy row is neither disabled nor invitable, and saying
                  either would be false. It gets its own sentence. */}
              {m.notice && <p className="text-[11px] text-faint mt-1">{m.notice}</p>}

              {confirmingDisable === m.user_id && (
                <div className="mt-2 border border-hairline rounded p-2.5 bg-fill-subtle">
                  <p className="text-[12px] text-body mb-2">
                    Remove access for {m.email ?? m.user_id}? They are signed out immediately,
                    and any invitation link still in their inbox stops working.
                  </p>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => disable.mutate(m.user_id)}
                      loading={disable.isPending}
                      className="text-[12px]"
                    >
                      Remove access
                    </Button>
                    <button
                      type="button"
                      onClick={() => setConfirmingDisable(null)}
                      className="text-[12px] text-muted hover:text-ink"
                    >
                      Keep access
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {(members.data?.members ?? []).length === 0 && (
            <div className="px-3 py-3 text-[12px] text-muted">Nobody has been invited yet.</div>
          )}
        </div>
      )}

      {/* ── Invitations ──────────────────────────────────────────────
          Spent and cancelled invitations stay listed (§38.7). An access grant
          that disappears from the record once used leaves no way to answer who
          was invited, by whom, and when. */}
      <h3 className="text-[12px] font-semibold text-body mb-2">Invitations</h3>
      {invitations.isLoading ? (
        <Spinner />
      ) : (
        <div className="divide-y divide-hairline border border-hairline rounded-md">
          {(invitations.data?.invitations ?? []).map((i) => (
            <div key={i.link_id} className="px-3 py-2 flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <div className="text-[12px] text-ink truncate">{i.email}</div>
                <div className="text-[11px] text-muted">
                  {profileLabel(i.profile)} · {STATE_LABEL[i.state] ?? i.state}
                </div>
              </div>
              {i.state === 'pending' && (
                <button
                  type="button"
                  onClick={() => revoke.mutate(i.link_id)}
                  className="flex-none text-[12px] text-muted hover:text-critical transition-colors"
                >
                  Cancel
                </button>
              )}
            </div>
          ))}
          {(invitations.data?.invitations ?? []).length === 0 && (
            <div className="px-3 py-3 text-[12px] text-muted">No invitations yet.</div>
          )}
        </div>
      )}
    </div>
  )
}
